import React, { useEffect, useRef, useState } from 'react';
import './styles.scss';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { scheme } from './validation';
import {
  FileInput,
  Flex,
  LoadingOverlay,
  Select,
  Text,
  TextInput
} from '@mantine/core';
import dayjs from 'dayjs';
import * as Unicons from '@iconscout/react-unicons';
import Button from '../../components/elements/Button';
import { DatePickerInput } from '@mantine/dates';
import { useQuery } from '@tanstack/react-query';
import { getLevel } from '../../services/pointManagement';

interface FormPointManagementProps {
  type?: string;
  data?: any;
  loading?: boolean;
  listLevel: any;
  onClose: () => void;
  handleSubmitData: (data: any) => void;
}

interface PointManagementData {
  level: string;
  multiplier: string;
  startDate: Date;
  endDate: Date;
}

const FormPointManagement: React.FC<FormPointManagementProps> = ({
  onClose,
  loading,
  type,
  listLevel,
  data,
  handleSubmitData
}) => {
  const [valueStartDate, setValueStartDate] = useState<Date | null>(
    data?.startDate ? new Date(data?.startDate) : null
  );

  const [valueEndDate, setValueEndDate] = useState<Date | null>(
    data?.endDate ? new Date(data?.endDate) : null
  );

  const {
    control,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<PointManagementData>({
    defaultValues: {
      level: data?.levelName || '',
      multiplier: data?.multiplier || '',
      startDate: data?.startDate || '',
      endDate: data?.endDate || ''
    }
    // resolver: yupResolver(scheme),
  });

  useEffect(() => {
    setValueStartDate(
      data?.startDate
        ? new Date(convertToLocalTime(data?.startDate + 'Z'))
        : null
    );
    setValueEndDate(
      data?.endDate ? new Date(convertToLocalTime(data?.endDate + 'Z')) : null
    );
  }, []);

  const handleSubmitForm: SubmitHandler<PointManagementData> = (values) => {
    // handleSubmitData(values);
    handleSubmitData({
      ...values,
      startDate: dayjs(valueStartDate).format('YYYY-MM-DD'),
      endDate: dayjs(valueEndDate).format('YYYY-MM-DD')
    });
  };

  const handleChangeDate = (date: any, type: string, field: any) => {
    field.onChange(date);
    if (type === 'startDate') setValueStartDate(date);
    if (type === 'endDate') setValueEndDate(date);
    return;
  };

  return (
    <>
      <section className="master-category-content__form">
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <Controller
            control={control}
            rules={{
              required: true
            }}
            render={({ field }) => (
              <div>
                <Select
                  className={
                    errors.level?.message &&
                    'master-rater__form__selectinput--error'
                  }
                  label="Level"
                  withAsterisk
                  placeholder="Select Level"
                  onChange={field.onChange}
                  value={field.value}
                  data={listLevel.map((item: any) => ({
                    value: item?.levelName,
                    label: item?.levelName
                  }))}
                />
                {errors?.level?.message && (
                  <Text size="xs" c="red" fw={400}>
                    {errors.level.message}
                  </Text>
                )}
              </div>
            )}
            name="level"
          />

          <TextInput
            label="Multiplier"
            radius={10}
            withAsterisk
            placeholder="Input multiplier (ex: 1.5)"
            error={errors.multiplier?.message}
            {...register('multiplier')}
          />

          <Controller
            control={control}
            rules={{
              required: true
            }}
            render={({ field }) => (
              <Flex flex={1} direction="column">
                <DatePickerInput
                  className={
                    errors.startDate?.message &&
                    'schedule-rater__form__selectinput--error'
                  }
                  style={{
                    width: '100%'
                  }}
                  label="Start Date"
                  withAsterisk
                  placeholder="Select Date"
                  onChange={(date) =>
                    handleChangeDate(date, 'startDate', field)
                  }
                  minDate={dayjs().toDate()}
                  value={valueStartDate}
                  rightSection={<Unicons.UilCalendarAlt size={20} />}
                />
                {errors?.startDate?.message && (
                  <Text size="xs" c="red" fw={400}>
                    {errors.startDate.message}
                  </Text>
                )}
              </Flex>
            )}
            name="startDate"
          />

          <Controller
            control={control}
            rules={{
              required: true
            }}
            render={({ field }) => (
              <Flex flex={1} direction="column">
                <DatePickerInput
                  className={
                    errors.endDate?.message &&
                    'schedule-rater__form__selectinput--error'
                  }
                  style={{
                    width: '100%'
                  }}
                  label="End Date"
                  withAsterisk
                  placeholder="Select Date"
                  onChange={(date) => handleChangeDate(date, 'endDate', field)}
                  minDate={dayjs().toDate()}
                  value={valueEndDate}
                  rightSection={<Unicons.UilCalendarAlt size={20} />}
                />
                {errors?.endDate?.message && (
                  <Text size="xs" c="red" fw={400}>
                    {errors.endDate.message}
                  </Text>
                )}
              </Flex>
            )}
            name="endDate"
          />
          <Flex justify="flex-end" align="center" gap={16} mt={32}>
            <Button variant="error-beelajar" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={loading} type="submit">
              {type === 'add' ? 'Submit' : 'Update'}
            </Button>
          </Flex>
        </form>
      </section>
      <LoadingOverlay visible={loading} />
    </>
  );
};

function convertToLocalTime(dateString) {
  const date = new Date(dateString);
  const localDate = date.toLocaleString();
  return localDate;
}

export default FormPointManagement;
